import { IPivotProps as FIPivotProps, IPivotStyles, Pivot as FPivot, PivotItem } from '@fluentui/react';
import { forwardRef, ReactNode, Ref } from 'react';

import { IComponent, IStyleFunctionOrObject } from '../../models/components.models';
import { useClassNames, useThemeStyles } from '../../utils/hooks';
import { combineClassNames, singleOrArrayToArray } from '../../utils/utils';
import { Loader } from '../Loader/Loader';
import { loaderStylesSpinnerPivot } from '../Loader/Loader.styles';
import {
  IPivotContainerClassNames,
  IPivotContainerStyles,
  pivotContainerStylesDefault,
  pivotStylesDefault,
  pivotStylesVertical,
} from './Pivot.styles';

export interface IPivotItem {
  headerText: string;
  key?: string | number;
  content?: ReactNode;
  loading?: boolean;
  iconName?: string;
}

export interface IPivotProps extends Omit<FIPivotProps, 'styles'>, IComponent<Partial<IPivotStyles>> {
  pivotContainerStyles?:
    | IStyleFunctionOrObject<IPivotContainerStyles>
    | IStyleFunctionOrObject<IPivotContainerStyles>[];
  items: IPivotItem[];
  vertical?: boolean;
  overflowAriaLabel?: string;
}

export const Pivot = forwardRef((props: IPivotProps, ref: Ref<HTMLDivElement>) => {
  const { styles, pivotContainerStyles, items, className, vertical, ..._fProps } = props;
  const classNames = useClassNames<IPivotContainerStyles, IPivotContainerClassNames>(
    'PivotContainer',
    pivotContainerStylesDefault,
    pivotContainerStyles,
    className,
    vertical && 'vertical'
  );
  const fProps: FIPivotProps = {
    ..._fProps,
    className: combineClassNames(className, 'ms-Pivot-wrapper'),
    styles: useThemeStyles<Partial<IPivotStyles>>(pivotStylesDefault, [
      ...(vertical ? [pivotStylesVertical] : []),
      ...singleOrArrayToArray(styles),
    ]),
  };
  const onRenderItems = () =>
    items.map(({ key = undefined, headerText, content, loading, iconName }, index) => (
      <PivotItem
        key={key === undefined ? index : key}
        itemKey={`${key === undefined ? index : key}`}
        itemIcon={iconName}
        headerText={headerText}
        headerButtonProps={{
          styles: {
            root: {
              pointerEvents: loading ? 'none' : '',
            },
          },
        }}
        onRenderItemLink={
          loading
            ? (itemProps, defaultRenderer) => (
                <div style={{ display: 'flex' }}>
                  {defaultRenderer!({ ...itemProps })}
                  {loading && <Loader styles={loaderStylesSpinnerPivot} />}
                </div>
              )
            : undefined
        }
      >
        {content}
      </PivotItem>
    ));
  return (
    <div ref={ref} className={classNames.root} data-test={props['data-test'] || 'pivot'}>
      <FPivot {...fProps}>{onRenderItems()}</FPivot>
    </div>
  );
});
